const EUR_FORMATTER = new Intl.NumberFormat("de-AT", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 2,
});

export function toNumber(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * A number the way people type it here: "1.240,00" and "1240.5" both mean the
 * same amount. Only a comma marks the decimals, so dots are separators then.
 */
export function parseAmount(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  return toNumber(trimmed.includes(",") ? trimmed.replace(/\./g, "").replace(",", ".") : trimmed);
}

export function formatEuro(value: string | number | null | undefined): string {
  const parsed = toNumber(value);
  if (parsed === null) {
    return "–";
  }
  return EUR_FORMATTER.format(parsed);
}

const QTY_FORMATTER = new Intl.NumberFormat("de-AT", {
  maximumFractionDigits: 2,
});

export function formatQty(value: string | number | null | undefined): string {
  const parsed = toNumber(value);
  if (parsed === null) {
    return "–";
  }
  return QTY_FORMATTER.format(parsed);
}

const PCT_FORMATTER = new Intl.NumberFormat("de-AT", {
  style: "percent",
  maximumFractionDigits: 1,
});

const MONTH_YEAR_FORMATTER = new Intl.DateTimeFormat("de-AT", {
  month: "short",
  timeZone: "UTC",
  year: "numeric",
});

export function formatPercent(
  value: string | number | null | undefined,
  fraction: "ratio" | "percent" = "percent",
): string {
  const parsed = toNumber(value);
  if (parsed === null) {
    return "–";
  }
  const ratio = fraction === "ratio" ? parsed : parsed / 100;
  return PCT_FORMATTER.format(ratio);
}

const SIGN_EPSILON = 1e-9;

/** Sign rule for deviations: "+" costs more, "−" saves; zero carries no sign. */
export function deviationSign(value: string | number | null | undefined): "+" | "−" | "" {
  const parsed = toNumber(value);
  if (parsed === null) return "";
  return parsed > SIGN_EPSILON ? "+" : parsed < -SIGN_EPSILON ? "−" : "";
}

export function formatSignedEuro(value: string | number | null | undefined): string {
  const parsed = toNumber(value);
  if (parsed === null) return "–";
  return `${deviationSign(parsed)}${EUR_FORMATTER.format(Math.abs(parsed))}`;
}

export function formatSignedPercent(value: string | number | null | undefined): string {
  const parsed = toNumber(value);
  if (parsed === null) return "–";
  return `${deviationSign(parsed)}${PCT_FORMATTER.format(Math.abs(parsed) / 100)}`;
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return "–";
  return value.slice(0, 10);
}

const DATE_SHORT_FORMATTER = new Intl.DateTimeFormat("de-AT", {
  day: "2-digit",
  month: "2-digit",
  year: "2-digit",
  timeZone: "UTC",
});

export function formatDateShort(value: string | null | undefined): string {
  if (!value) return "–";
  const iso = value.slice(0, 10);
  const [yearRaw, monthRaw, dayRaw] = iso.split("-", 3);
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const day = Number(dayRaw);
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return formatDate(value);
  }
  return DATE_SHORT_FORMATTER.format(new Date(Date.UTC(year, month - 1, day)));
}

const DATE_TIME_SHORT_FORMATTER = new Intl.DateTimeFormat("de-AT", {
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  month: "2-digit",
  timeZone: "Europe/Vienna",
  year: "2-digit",
});

export function formatDateTimeShort(
  value: string | null | undefined,
): string {
  if (!value) return "–";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return formatDateShort(value);
  return DATE_TIME_SHORT_FORMATTER.format(date);
}

export function formatMonthYear(value: string | null | undefined): string {
  if (!value) return "–";
  const [yearRaw, monthRaw] = value.slice(0, 10).split("-", 2);
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    month < 1 ||
    month > 12
  ) {
    return formatDate(value);
  }
  return MONTH_YEAR_FORMATTER.format(new Date(Date.UTC(year, month - 1, 1)));
}

export function formatMonthRange(
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  const startLabel = formatMonthYear(start);
  const endLabel = formatMonthYear(end);
  if (startLabel === "–" && endLabel === "–") return "–";
  if (startLabel === "–") return `bis ${endLabel}`;
  if (endLabel === "–") return `ab ${startLabel}`;
  return `${startLabel} bis ${endLabel}`;
}
